import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MicrosoftIcon from '@mui/icons-material/Microsoft';

/**
 * Custom Windows/AD credential dialog — domain is prefilled from login-config.
 */
export default function WindowsLoginModal({
    open,
    onClose,
    onSubmit,
    defaultDomain = '',
    loading = false,
    error = '',
}) {
    const [domain, setDomain] = useState(defaultDomain || '');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (open) {
            setDomain(defaultDomain || '');
            setUsername('');
            setPassword('');
            setShowPassword(false);
        }
    }, [open, defaultDomain]);

    const hidePassword = () => setShowPassword(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit?.({
            domain: (domain || '').trim(),
            username: (username || '').trim(),
            password,
        });
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MicrosoftIcon fontSize="small" />
                Sign in with Windows
            </DialogTitle>
            <Box component="form" onSubmit={handleSubmit}>
                <DialogContent>
                    <Stack spacing={2}>
                        {error ? <Alert severity="error">{error}</Alert> : null}
                        <FormControl>
                            <FormLabel htmlFor="win-domain">Domain</FormLabel>
                            <TextField
                                id="win-domain"
                                name="domain"
                                fullWidth
                                size="small"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                disabled={loading}
                                placeholder="Detected automatically"
                                autoComplete="organization"
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel htmlFor="win-username">Username</FormLabel>
                            <TextField
                                id="win-username"
                                name="username"
                                fullWidth
                                size="small"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={loading}
                                autoFocus
                                required
                                autoComplete="username"
                                placeholder="sAMAccountName"
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel htmlFor="win-password">Password</FormLabel>
                            <TextField
                                id="win-password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                fullWidth
                                size="small"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                required
                                autoComplete="current-password"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Hold to show password"
                                                edge="end"
                                                disabled={loading}
                                                onMouseDown={(ev) => {
                                                    ev.preventDefault();
                                                    setShowPassword(true);
                                                }}
                                                onMouseUp={hidePassword}
                                                onMouseLeave={hidePassword}
                                                onTouchStart={(ev) => {
                                                    ev.preventDefault();
                                                    setShowPassword(true);
                                                }}
                                                onTouchEnd={hidePassword}
                                            >
                                                <VisibilityOutlinedIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={loading || !username || !password}>
                        Sign in
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
